import * as THREE from 'three';
import { DEBUG, SPAWN } from '../config/tuning';
import { AIRFRAME_SCOUT } from '../config/airframes';
import { Checksum } from '../core/Checksum';
import { Loop } from '../core/Loop';
import { Atmosphere } from '../flight/Atmosphere';
import { FlightModel } from '../flight/FlightModel';
import { createControlInput, type ControlInput } from '../flight/FlightState';

/**
 * The determinism proof: the same scripted flight, flown twice, compared tick by tick.
 *
 * At M0 this exercised a spinning cube. It now flies the actual flight model through a
 * manoeuvring script — climb, hard break, unload, roll — because that is what has to stay
 * reproducible. Comparing only the final state would hide a divergence that happens to cancel;
 * comparing every tick gives the exact tick where the runs parted, which is the only fact worth
 * having when this fails during M6 guidance work.
 *
 * Run A is driven at a steady 60 fps, run B with a vicious frame-delta sequence including a
 * 0.4 s stall past the loop's clamp. Identical checksums prove that simulation state depends on
 * the tick count alone, never on frame timing.
 */

export interface SelfTestResult {
  passed: boolean;
  ticks: number;
  /** First tick whose checksums differed, or −1 when the runs matched. */
  firstDivergenceTick: number;
  checksumA: number;
  checksumB: number;
}

export const STEADY_FRAME_DELTAS_S: readonly number[] = [1 / 60];

export const IRREGULAR_FRAME_DELTAS_S: readonly number[] = [
  1 / 60,
  1 / 240,
  0.4,
  1 / 90,
  1 / 30,
  0.006,
  1 / 144,
];

/**
 * A scripted control sequence, as a pure function of tick index. Deliberately covers the corners
 * that break first: sustained back pressure into the stall, a full-deflection roll, and an
 * unload.
 */
export function scriptedInput(tick: number, out: ControlInput): ControlInput {
  const phase = tick % 1200;
  if (phase < 200) {
    // Trimmed cruise.
    out.rollCmd = 0;
    out.pitchCmd = 0;
    out.yawCmd = 0;
    out.throttle = SPAWN.throttle;
  } else if (phase < 480) {
    // Hard banked break: roll in, then hold heavy back pressure.
    out.rollCmd = phase < 280 ? 1 : 0.08;
    out.pitchCmd = 0.85;
    out.yawCmd = 0;
    out.throttle = 1;
  } else if (phase < 700) {
    // Unload and roll out the other way.
    out.rollCmd = -1;
    out.pitchCmd = -0.15;
    out.yawCmd = 0.25;
    out.throttle = 0.4;
  } else if (phase < 950) {
    // Slow flight into a departure: idle, nose up, a boot of rudder to drop a wing.
    out.rollCmd = 0;
    out.pitchCmd = 1;
    out.yawCmd = 0.5;
    out.throttle = 0;
  } else {
    // Recovery: unload, centralise, power up.
    out.rollCmd = 0;
    out.pitchCmd = -0.4;
    out.yawCmd = 0;
    out.throttle = 1;
  }
  return out;
}

/** Build a flight model at the standard spawn condition for a given seed. */
export function createScriptedFlight(seed: number): FlightModel {
  const atmosphere = new Atmosphere(seed);
  const model = new FlightModel(AIRFRAME_SCOUT, atmosphere);
  const [x, y, z] = SPAWN.positionM;
  model.spawn({
    positionM: new THREE.Vector3(x, y, z),
    headingRad: (SPAWN.headingDeg * Math.PI) / 180,
    airspeedMs: SPAWN.airspeedMs,
    throttle: SPAWN.throttle,
  });
  return model;
}

/**
 * Run the scripted flight until `ticks` ticks have executed, recording the state checksum after
 * each one. Recording happens inside fixedUpdate, so every tick is captured regardless of how
 * many a given frame runs.
 */
export function recordTickChecksums(
  seed: number,
  ticks: number,
  frameDeltasS: readonly number[],
): Uint32Array {
  const model = createScriptedFlight(seed);
  const input = createControlInput();
  const sum = new Checksum();
  const checksums = new Uint32Array(ticks);
  let recorded = 0;

  const loop = new Loop({
    fixedUpdate: (dtS, time) => {
      scriptedInput(time.tick, input);
      model.tick(dtS, input);
      if (recorded < ticks) {
        sum.reset();
        model.checksum(sum);
        checksums[recorded] = sum.value;
        recorded += 1;
      }
    },
    render: () => {},
  });

  let frame = 0;
  while (recorded < ticks) {
    loop.advance(frameDeltasS[frame % frameDeltasS.length]);
    frame += 1;
  }
  return checksums;
}

/** Fly the scripted flight twice from the same seed and compare each tick's state checksum. */
export function runDeterminismSelfTest(
  seed: number,
  ticks: number = DEBUG.SELFTEST_TICKS,
): SelfTestResult {
  const checksumsA = recordTickChecksums(seed, ticks, STEADY_FRAME_DELTAS_S);
  const checksumsB = recordTickChecksums(seed, ticks, IRREGULAR_FRAME_DELTAS_S);

  let firstDivergenceTick = -1;
  for (let tick = 0; tick < ticks; tick += 1) {
    if (checksumsA[tick] !== checksumsB[tick]) {
      firstDivergenceTick = tick;
      break;
    }
  }

  return {
    passed: firstDivergenceTick === -1,
    ticks,
    firstDivergenceTick,
    checksumA: checksumsA[ticks - 1],
    checksumB: checksumsB[ticks - 1],
  };
}

/** Format a self-test result as a single console line. */
export function formatSelfTestResult(result: SelfTestResult): string {
  const hexA = result.checksumA.toString(16).toUpperCase().padStart(8, '0');
  const hexB = result.checksumB.toString(16).toUpperCase().padStart(8, '0');
  if (result.passed) {
    return `[determinism] PASS — ${result.ticks} ticks identical, final checksum 0x${hexA}`;
  }
  return `[determinism] FAIL — diverged at tick ${result.firstDivergenceTick} (A 0x${hexA} / B 0x${hexB})`;
}
